import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, User, Users, Heart, HeartHandshake, BookOpen, FolderKanban, BarChart3, Settings as SettingsIcon, Sparkles, ScrollText, FileText, Maximize2, Minimize2, BookMarked } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePresenterMode } from "@/hooks/usePresenterMode";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import SchoolsCommand from "@/components/schools/SchoolsCommand";
import RoleSwitcher from "@/components/schools/RoleSwitcher";
import PageTransition from "@/components/schools/PageTransition";
import PresenterHud from "@/components/schools/PresenterHud";
import ShortcutsOverlay from "@/components/schools/ShortcutsOverlay";
import AutoplayBar from "@/components/schools/AutoplayBar";
import SpeakerNotes from "@/components/schools/SpeakerNotes";
import { school } from "@/data/greenwood";

const nav = [
  { to: "/schools", label: "Principal Overview", icon: LayoutDashboard, end: true, group: "Executive" },
  { to: "/schools/students", label: "Student Roster", icon: Users, group: "Personas" },
  { to: "/schools/student", label: "Student Profile", icon: User, group: "Personas" },
  { to: "/schools/parent", label: "Parent Dashboard", icon: Heart, group: "Personas" },
  { to: "/schools/counsellor", label: "Counsellor", icon: HeartHandshake, group: "Personas" },
  { to: "/schools/studios", label: "Learning Studios", icon: BookOpen, group: "Programme" },
  { to: "/schools/portfolio", label: "Portfolio", icon: FolderKanban, group: "Programme" },
  { to: "/schools/reports", label: "Reports", icon: BarChart3, group: "Programme" },
  { to: "/schools/glossary", label: "Glossary", icon: BookMarked, group: "Programme" },
  { to: "/schools/settings", label: "Settings", icon: SettingsIcon, group: "Programme" },
];

const navGroups = ["Executive", "Personas", "Programme"] as const;

export default function SchoolsShell() {
  const { pathname } = useLocation();
  const presenter = usePresenterMode();
  return (
    <SidebarProvider defaultOpen={!presenter.on}>
      <div
        className={`schools-theme min-h-screen flex w-full bg-background ${presenter.on ? "presenter-mode" : ""}`}
        data-presenter={presenter.on ? "on" : "off"}
      >
        {!presenter.on && (
          <Sidebar collapsible="icon">
            <SidebarHeader className="border-b">
              <div className="flex items-center gap-2 px-2 py-3">
                <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold leading-tight truncate" title={school.name}>{school.name}</div>
                  <div className="text-[11px] text-muted-foreground leading-tight truncate" title={school.principal}>{school.principal}</div>
                  <div className="text-[10px] text-muted-foreground/80 truncate mt-0.5">WeKIT Education Intelligence™ · WCC360</div>
                </div>
              </div>
            </SidebarHeader>
            <SidebarContent>
              {navGroups.map((group) => (
                <SidebarGroup key={group}>
                  <SidebarGroupLabel>{group}</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {nav.filter((n) => n.group === group).map((item) => {
                        const active = item.end ? pathname === item.to : pathname.startsWith(item.to);
                        return (
                          <SidebarMenuItem key={item.to}>
                            <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                              <NavLink to={item.to} end={item.end} className="flex items-center gap-2">
                                <item.icon className="w-4 h-4" />
                                <span>{item.label}</span>
                              </NavLink>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </SidebarContent>
            <SidebarFooter className="border-t">
              <div className="px-2 py-3 space-y-2">
                <Link
                  to="/schools/script"
                  className="schools-cta-primary block w-full text-center px-3 py-2.5 text-sm font-semibold shadow-sm"
                >
                  Help Center
                </Link>
                <div className="px-1 text-[10px] text-muted-foreground">Demo environment · v1.0</div>
              </div>
            </SidebarFooter>

          </Sidebar>
        )}


        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center gap-2 sm:gap-3 border-b bg-background/80 backdrop-blur px-3 sm:px-4 sticky top-0 z-10 no-print">
            {!presenter.on && <SidebarTrigger />}
            <div className="hidden lg:flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="truncate">Greenwood International School · AY 2026–27</span>
            </div>
            <div className="flex-1" />
            <RoleSwitcher />
            <SchoolsCommand />
            {!presenter.on && (
              <>
                <Button asChild size="sm" variant="ghost" className="hidden md:inline-flex">
                  <Link to="/schools/script"><ScrollText className="w-3.5 h-3.5 mr-1" /> Demo script</Link>
                </Button>
                <Button asChild size="sm" variant="ghost" className="hidden lg:inline-flex">
                  <Link to="/schools/boardpack"><FileText className="w-3.5 h-3.5 mr-1" /> Board pack</Link>
                </Button>
              </>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant={presenter.on ? "default" : "ghost"}
                    onClick={presenter.toggle}
                    aria-label={presenter.on ? "Exit presenter mode" : "Enter presenter mode"}
                    aria-pressed={presenter.on}
                    className="gap-1"
                  >
                    {presenter.on ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{presenter.on ? "Exit" : "Present"}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {presenter.on ? "Exit presenter mode (Esc)" : "Enter presenter mode (Shift+P)"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {!presenter.on && (
              <>
                {pathname.startsWith("/schools/live") ? (
                  <Button asChild size="sm" variant="outline" className="hidden md:inline-flex">
                    <Link to="/schools">Demo tenant</Link>
                  </Button>
                ) : (
                  <Button asChild size="sm" variant="outline" className="hidden md:inline-flex">
                    <Link to="/schools/live">Live tenant</Link>
                  </Button>
                )}
                <Badge variant="secondary" className="hidden md:inline-flex rounded-full">
                  {pathname.startsWith("/schools/live") ? "Live" : "Demo"}
                </Badge>
              </>
            )}
          </header>

          <main className="flex-1">
            <PageTransition>
              <Outlet />
            </PageTransition>
          </main>
          <PresenterHud on={presenter.on} />
          <ShortcutsOverlay />
          <AutoplayBar />
          <SpeakerNotes />
        </div>
      </div>
    </SidebarProvider>
  );
}
